import { HandoffResumeImportDraft } from './HandoffResumeImportDraft';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@tabbio/tokens';
import { useOnboarding } from './OnboardingProvider';
import { trackProductEvent } from '../../lib/product-analytics';
import { abandonPendingResumeImport } from '../../lib/resume-handoff';
import { confirm } from '../../lib/alert.web';
import { ResumeImportComposer } from '../cv/ResumeImportComposer';
import { TabbioMark } from '../common/TabbioMark';
import { CvPresetSheet } from './CvPresetSheet';
import { CvPresetAppliedChip, CvPresetPill } from './CvPresetControls';
import { useCvPreset } from './use-cv-preset';
import { useOnboardingCvHandoffClaim } from './use-onboarding-cv-handoff-claim';
import { useOnboardingResumeHandoffClaim } from './use-onboarding-resume-handoff-claim';
import { EMPLOYER_WORKSPACES_ENABLED } from '../../lib/launch-scope';
import { HiringCompanySetup } from './HiringCompanySetup.web';

export function StepAIInput() {
  const { t } = useTranslation('auth');
  const { persistImportedCvAndGoToHeader, startManualCvBuild } =
    useOnboarding();
  const preset = useCvPreset();
  // A pending CV-handoff invite auto-claims here (intent 'update') and jumps
  // to the preview; while it runs the composer stays disabled, like the
  // resume-handoff auto-import below.
  const { isClaimingCvHandoff } = useOnboardingCvHandoffClaim();
  const [manualHovered, setManualHovered] = useState(false);
  const [hiringHovered, setHiringHovered] = useState(false);
  const [isHiringSetupOpen, setIsHiringSetupOpen] = useState(false);
  // Whether the composer's pending draft is a file: the parse reads the file
  // when one is attached, so this names the import source for CV Health.
  const importHasFileRef = useRef(false);

  /**
   * Review step for a CV parsed WITHOUT AI (the provider was unavailable, so
   * the exact extracted details stand in). The user confirms before those
   * details replace anything; declining keeps the composer for another try.
   */
  const confirmExactExtraction = useCallback(
    () =>
      confirm({
        title: t('onboarding.cvJourney.exactExtractionTitle', {
          defaultValue: 'Imported without AI',
        }),
        message: t('onboarding.cvJourney.exactExtractionMessage', {
          defaultValue:
            'Our AI writer was unavailable, so we used the exact details from your CV without rewriting them. You can review and improve them in the next steps.',
        }),
        confirmLabel: t('onboarding.cvJourney.exactExtractionContinue', {
          defaultValue: 'Use exact details',
        }),
        cancelLabel: t('onboarding.cvJourney.exactExtractionCancel', {
          defaultValue: 'Not now',
        }),
      }),
    [t],
  );

  // A resume uploaded on the website (landing hero, skill page, public CV
  // Health) is imported here, once, and only for the sign-in that came from
  // that funnel; the hook owns the stale-token and failure rules.
  const { isClaimingHandoff, pendingImport, sourceChanged, resumePendingImport } = useOnboardingResumeHandoffClaim({
    confirmExactExtraction,
  });
  const isBusy = isClaimingHandoff || isClaimingCvHandoff;

  const handleManualBuild = () => {
    if (isBusy) {
      return;
    }
    trackProductEvent('onboarding_build_manually', {});
    // Building manually cancels a pending website resume import: the
    // provider's startManualCvBuild does that for native and web alike.
    startManualCvBuild();
  };

  const handleHiringOpen = () => {
    if (isClaimingHandoff) {
      return;
    }
    trackProductEvent('onboarding_hiring_selected', {});
    // Same hygiene as manual build: a hiring signup cancels a pending website
    // resume import; job/join/message handoffs are untouched.
    void abandonPendingResumeImport();
    setIsHiringSetupOpen(true);
  };

  return (
    // `.tabbio-hero` contributes only the skill-sanctioned faint radial glow
    // (its ::before layer, dark-mode aware via [data-theme]): the flex chrome
    // it declares for the home empty-state is overridden inline. overflow:hidden
    // clips the oversized glow so it never creates scrollbars; it is the web
    // stand-in for the native OnboardingBackground gradient, which is far too
    // brand-heavy for the neutral web palette.
    <div
      className="tabbio-hero"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        padding: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          padding: '24px 0',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* margin:auto centers without clipping the top when content is
            taller than the viewport (unlike justify-content:center). */}
        <div
          style={{
            maxWidth: 560,
            width: '100%',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Brand mark above the composer: mirrors the native ai-input hero. */}
          <div aria-hidden style={{ marginBottom: 28 }}>
            <TabbioMark size={40} color={colors.primary} />
          </div>
          <div style={{ width: '100%' }}>
            <HandoffResumeImportDraft draft={pendingImport} sourceChanged={sourceChanged} disabled={isBusy} onReview={() => void resumePendingImport()} />
            <ResumeImportComposer
              skipEnrichment
              title={t('onboarding.cvJourney.aiInputTitle', {
                defaultValue: "Let's Build Your CV",
              })}
              subtitle={t('onboarding.cvJourney.aiInputSubtitle', {
                defaultValue: 'Takes seconds. Share it anywhere.',
              })}
              placeholder={t('onboarding.cvJourney.pasteHere', {
                defaultValue:
                  'Upload or paste your CV, or just tell us what you do',
              })}
              parseFailedTitle={t('onboarding.cvJourney.parseFailedTitle', {
                defaultValue: 'Parse failed',
              })}
              parseFailedMessage={t('onboarding.cvJourney.parseFailed', {
                defaultValue:
                  'Could not process your input. Try again or fill manually.',
              })}
              disabled={isBusy}
              analyticsContext="onboarding"
              toolbarAccessory={
                <CvPresetPill
                  onPress={preset.openSheet}
                  isActive={Boolean(preset.appliedPresetId)}
                  disabled={isBusy}
                />
              }
              banner={
                preset.appliedLabel ? (
                  <CvPresetAppliedChip
                    presetLabel={preset.appliedLabel}
                    onChange={preset.openSheet}
                    onRemove={preset.clear}
                    disabled={isBusy}
                  />
                ) : null
              }
              onDraftChange={(draft) => {
                importHasFileRef.current = draft.hasFile;
              }}
              onParsed={async ({ parsed, quality, importEvidenceId }) => {
                // Exact extraction (no AI): review before it replaces anything.
                if (
                  quality?.fallbackUsed &&
                  !(await confirmExactExtraction())
                ) {
                  return;
                }
                return persistImportedCvAndGoToHeader(parsed, {
                  mode: 'push',
                  quality,
                  importEvidenceId,
                  source: importHasFileRef.current ? 'file' : 'text',
                });
              }}
            />
          </div>
        </div>
      </div>

      <CvPresetSheet
        visible={preset.isSheetOpen}
        onClose={preset.closeSheet}
        appliedPresetId={preset.appliedPresetId}
        entries={preset.rowEntries}
        onApply={(selected) => {
          void preset.apply(selected);
        }}
        isApplying={preset.isApplying}
      />

      {/* Separated bottom CTA: "or" divider + manual build, matching native. */}
      <div
        style={{
          flexShrink: 0,
          width: '100%',
          maxWidth: 560,
          marginInline: 'auto',
          padding: '0 0 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 1, backgroundColor: 'var(--input-border)' }} />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {t('onboarding.cvJourney.or', { defaultValue: 'or' })}
          </span>
          <div style={{ flex: 1, height: 1, backgroundColor: 'var(--input-border)' }} />
        </div>
        <button
          type="button"
          onClick={handleManualBuild}
          disabled={isBusy}
          onMouseEnter={() => setManualHovered(true)}
          onMouseLeave={() => setManualHovered(false)}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 9999,
            border: 'none',
            backgroundColor:
              manualHovered && !isBusy
                ? 'var(--hover-strong)'
                : 'transparent',
            color:
              manualHovered && !isBusy
                ? 'var(--text-primary)'
                : 'var(--text-secondary)',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: isBusy ? 'default' : 'pointer',
            opacity: isBusy ? 0.4 : 1,
            transition: 'background-color 150ms, color 150ms, opacity 150ms',
          }}
        >
          {t('onboarding.cvJourney.fillManually', {
            defaultValue: 'Build my CV manually',
          })}
        </button>
        {EMPLOYER_WORKSPACES_ENABLED ? (
          <button
            type="button"
            onClick={handleHiringOpen}
            disabled={isClaimingHandoff}
            onMouseEnter={() => setHiringHovered(true)}
            onMouseLeave={() => setHiringHovered(false)}
            style={{
              width: '100%',
              height: 32,
              border: 'none',
              backgroundColor: 'transparent',
              color:
                hiringHovered && !isClaimingHandoff
                  ? 'var(--text-secondary)'
                  : 'var(--text-tertiary)',
              fontSize: 12,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: isClaimingHandoff ? 'default' : 'pointer',
              opacity: isClaimingHandoff ? 0.4 : 1,
              transition: 'color 150ms, opacity 150ms',
            }}
          >
            {t('onboarding.cvJourney.hiringLink', {
              defaultValue: 'Hiring? Create a company page instead',
            })}
          </button>
        ) : null}
      </div>

      {EMPLOYER_WORKSPACES_ENABLED ? (
        <HiringCompanySetup
          visible={isHiringSetupOpen}
          onClose={() => setIsHiringSetupOpen(false)}
        />
      ) : null}
    </div>
  );
}
